import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  RadarController,
} from "chart.js";
import { motion } from "framer-motion";
import { HiOutlineChartBar } from "react-icons/hi2";
import { CriteriaBreakdown } from "types/assessmentTypes";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  RadarController
);

interface RadarChartProps {
  criteriaBreakdown: CriteriaBreakdown[];
  title?: string;
  description?: string;
  height?: number;
}

const NEGATIVE_KPIS = [
  "CCC",
  "EMPLOYEE_TURNOVER",
  "LOGISTICS_TO_SALES",
  "ORGANIZATION_SIZE",
];

export default function RadarChartComponent({
  criteriaBreakdown,
  title = "Radar Chart",
  description = "Performance indicators overview",
  height = 500,
}: RadarChartProps) {
  const { t, i18n } = useTranslation();
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<ChartJS | null>(null);

  const isCriteriaNegative = (criteria: CriteriaBreakdown): boolean => {
    return (
      criteria.kpis?.some((kpi) => NEGATIVE_KPIS.includes(kpi.code)) || false
    );
  };

  const getFinalPercent = (criteria: CriteriaBreakdown): number => {
    const correctedPercent = criteria.correctedPercent || 0;
    if (isCriteriaNegative(criteria)) {
      return Math.max(0, Math.min(100, 100 - correctedPercent));
    }
    return Math.max(0, Math.min(100, correctedPercent));
  };

  const hasData = criteriaBreakdown && criteriaBreakdown.length > 0;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortedData = hasData
    ? [...criteriaBreakdown]
        .sort((a, b) => a.criteriaIndex - b.criteriaIndex)
        .map((criteria) => {
          const finalPercent = getFinalPercent(criteria);
          return {
            label: t(`criteria.${criteria.criteriaIndex}`),
            value: Math.round(finalPercent * 10) / 10,
          };
        })
    : [];

  useEffect(() => {
    if (!chartRef.current || !hasData) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const ctx = chartRef.current.getContext("2d");
    if (!ctx) return;

    const isRTL = i18n.dir() === "rtl";

    chartInstanceRef.current = new ChartJS(ctx, {
      type: "radar",
      data: {
        labels: sortedData.map((item) => item.label),
        datasets: [
          {
            label: "Performance",
            data: sortedData.map((item) => item.value),
            backgroundColor: "rgba(99, 102, 241, 0.2)",
            borderColor: "rgba(99, 102, 241, 1)",
            borderWidth: 2,
            pointBackgroundColor: "rgba(99, 102, 241, 1)",
            pointBorderColor: "rgba(99, 102, 241, 1)",
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,

        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            titleColor: "#1F2937",
            bodyColor: "#6B7280",
            borderColor: "#E5E7EB",
            borderWidth: 1,
            titleFont: {
              size: 16,
              family: isRTL ? "IRANSans" : "inherit",
            },
            padding: 12,
            cornerRadius: 8,
            callbacks: {
              label: function (context: any) {
                return `${context.parsed.r} %`;
              },
            },
          },
        },
        scales: {
          r: {
            min: 0,
            max: 100,
            ticks: {
              stepSize: 20,
              color: "#9CA3AF",
              font: {
                size: 16,
                family: isRTL ? "IRANSans" : "inherit",
              },
              backdropColor: "transparent",
            },
            pointLabels: {
              color: "#6B7280",
              font: {
                size: 13,
                weight: 700,
                family: isRTL ? "IRANSans" : "inherit",
              },
            },
            grid: {
              color: "#E5E7EB",
              circular: true,
            },
            angleLines: {
              color: "#E5E7EB",
            },
          },
        },
      },
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [sortedData, hasData, i18n]);

  if (!hasData) {
    return (
      <div className="flex h-64 items-center justify-center rounded-2xl bg-white p-6 shadow-lg dark:bg-navy-800">
        <p className="text-gray-500 dark:text-gray-400">{t("table.noData")}</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="hidden rounded-2xl bg-gray-100/50 p-6 shadow-lg dark:bg-navy-800 md:block"
    >
      <div className="mb-6 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-2.5 shadow-lg shadow-brand-500/25">
            <HiOutlineChartBar className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              {title}
            </h3>
            <p className="text-xs text-gray-700 dark:text-gray-400">
              {description} ({criteriaBreakdown.length}{" "}
              {t("assessment.report.criteria")})
            </p>
          </div>
        </div>
      </div>

      <div style={{ height }}>
        <canvas ref={chartRef} />
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-4 border-t border-gray-100 pt-4 dark:border-navy-700">
        <div className="flex items-center gap-2">
          <div
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: "#818CF8" }}
          />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t("assessment.report.performance") || "Performance"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-2 w-8 rounded-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500" />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            0% ← 100%
          </span>
        </div>
      </div>
    </motion.div>
  );
}
